/**
 * DCP camera-model registry — Phase 3.B-color P4.
 *
 * Looks up a DCP file (DNG Camera Profile) for a given camera
 * (make + model) reported by libraw's metadata. Returns the raw blob
 * so the caller can pass it to `parseDCP()`.
 *
 * Storage layout (under `public/vendor/dcp/`):
 *
 *   index.json               { "Canon EOS R5": "canon/eos-r5.dcp", ... }
 *   canon/eos-r5.dcp         Adobe-shipped DCP, content-stable, sha256 in SOURCES.md
 *   …
 *   user/                    user-dropped DCPs (not in index — manual lookup)
 *
 * Lazy-fetch protocol:
 *   1. First call fetches `index.json` once. Cached on the module.
 *   2. Per camera: fetch the .dcp blob from the vendor path.
 *   3. Returns null on any miss / network failure — caller MUST handle
 *      null gracefully (fall back to existing ingestLinearRec709).
 *
 * Privacy: all fetches are same-origin (relative `/vendor/dcp/...`).
 * No CDN, no analytics. Service-worker route in `src/sw.js` caches
 * these for offline use after the first hit.
 *
 * Build-time: `index.json` is generated by `scripts/build-dcp-index.mjs`
 * (Phase 3.B-color P4 follow-up). For v1 we ship a static index;
 * if no DCPs are vendored, the file is `{}` and every lookup misses.
 *
 * @module src/pipeline/dcp/registry
 */

const INDEX_URL = '/vendor/dcp/index.json';
const DCP_BASE  = '/vendor/dcp/';

/* In-flight + result caches keyed at module scope. */
let indexPromise = null;
let indexLoaded  = null;
const blobCache = new Map(); /* relPath → Promise<Blob|null> */

/**
 * Normalise a libraw "make"/"model" pair into a key the registry can
 * look up. Strips trailing whitespace + collapses multiple spaces.
 * Adobe + libraw both use canonical case ("Canon EOS R5", not "CANON
 * EOS R5"), so we don't lowercase — exact match is the v1 strategy.
 *
 * Future: a fuzzy lookup that strips "Mark" / "Mk" punctuation, etc.
 *
 * @param {string} make
 * @param {string} model
 * @returns {string}    e.g. "Canon EOS R5"
 */
export function modelKey(make, model) {
    const m = String(make ?? '').trim().replace(/\s+/g, ' ');
    let mod = String(model ?? '').trim().replace(/\s+/g, ' ');
    if (!m && !mod) return '';
    /* libraw sometimes prepends maker name to model, sometimes not.
     * Avoid double-prepending: if `model` already starts with `make`,
     * just use the model as-is. */
    if (mod && m && !mod.toLowerCase().startsWith(m.toLowerCase())) {
        return `${m} ${mod}`.trim();
    }
    return mod || m;
}

/**
 * Fetch + cache the DCP index map. Returns {} on any failure (so
 * lookups uniformly miss without bringing down the RAW decode path).
 *
 * @returns {Promise<Record<string, string>>}
 */
async function loadIndex() {
    if (indexLoaded) return indexLoaded;
    if (indexPromise) return indexPromise;
    indexPromise = (async () => {
        try {
            const r = await fetch(INDEX_URL, { cache: 'force-cache' });
            if (!r.ok) return {};
            const j = await r.json();
            return (j && typeof j === 'object') ? j : {};
        } catch {
            return {};
        }
    })().then((idx) => {
        indexLoaded = idx;
        return idx;
    });
    return indexPromise;
}

/**
 * Fetch a single DCP blob from `public/vendor/dcp/<relPath>`. Cached
 * per-relPath. Returns null on any failure.
 *
 * @param {string} relPath
 * @returns {Promise<Blob|null>}
 */
function fetchDCP(relPath) {
    if (blobCache.has(relPath)) return blobCache.get(relPath);
    const p = (async () => {
        try {
            /* Reject path traversal — vendor index is an attacker-side
             * input if the build-time generator is ever compromised. */
            if (typeof relPath !== 'string' || relPath.includes('..') || /^[/\\]/.test(relPath)) {
                return null;
            }
            const r = await fetch(DCP_BASE + relPath, { cache: 'force-cache' });
            if (!r.ok) return null;
            const blob = await r.blob();
            return blob;
        } catch {
            return null;
        }
    })();
    blobCache.set(relPath, p);
    return p;
}

/**
 * Find the DCP blob for a camera. Returns the raw blob (caller passes
 * to `parseDCP`) or null if no profile is registered.
 *
 * @param {string} make
 * @param {string} model
 * @returns {Promise<Blob|null>}
 */
export async function findDCPForCamera(make, model) {
    const key = modelKey(make, model);
    if (!key) return null;

    const index = await loadIndex();
    let relPath = index[key];

    /* Try a few fallbacks for libraw model-string variation. */
    if (!relPath) {
        /* Some Canon RAW files report "EOS R5" without "Canon " prefix
         * and vice versa. Try both. */
        const altKey = key.toLowerCase();
        for (const k of Object.keys(index)) {
            if (k.toLowerCase() === altKey) { relPath = index[k]; break; }
        }
    }
    if (!relPath) return null;

    return fetchDCP(relPath);
}

/**
 * Test-only reset hook so the test gate can clear caches between
 * assertions. NOT part of the public API surface.
 */
export function __resetCachesForTest() {
    indexPromise = null;
    indexLoaded = null;
    blobCache.clear();
}
